import React from 'react';
import './OrganizationInfo.css';

export interface OrganizationInfoProps {
  organizations: Array<{
    id?: number;
    name?: string;
    url?: string;
    employeeCount?: number;
    naics?: string;
    country?: string;
    annualSpend?: number;
    revenue?: number;
    eslapId?: string;
    pollenBuyerId?: number;
    pollenId?: number;
    uuid?: string;
    created?: string;
    updated?: string;
  }>;
  count: number;
  context?: string;
}

export const OrganizationInfo: React.FC<OrganizationInfoProps> = ({ organizations, count, context }) => {
  if (organizations.length === 0) {
    return (
      <div className="organization-info-container">
        <div className="organization-info-header">
          <h3>🏛️ Organization Information</h3>
          <span className="organization-count">No organizations found</span>
        </div>
      </div>
    );
  }

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <div className="organization-info-container">
      <div className="organization-info-header">
        <h3>🏛️ Organization Information</h3>
        <span className="organization-count">
          {count} {count === 1 ? 'organization' : 'organizations'}
        </span>
      </div>
      {context && <p className="organization-info-context">{context}</p>}

      <div className="organizations-grid">
        {organizations.map((organization, index) => (
          <div key={organization.id || organization.uuid || index} className="organization-card">
            <div className="organization-card-header">
              <div className="organization-icon">🏛️</div>
              <div className="organization-name-section">
                <h4 className="organization-name">{organization.name || 'Unnamed Organization'}</h4>
                {organization.url && (
                  <div className="organization-url">
                    <a href={organization.url} target="_blank" rel="noopener noreferrer">
                      {organization.url}
                    </a>
                  </div>
                )}
              </div>
            </div>

            <div className="organization-details">
              {organization.employeeCount !== undefined && (
                <div className="organization-detail-item">
                  <span className="detail-label">Employees:</span>
                  <span className="detail-value">
                    {new Intl.NumberFormat('en-US').format(organization.employeeCount)}
                  </span>
                </div>
              )}

              {organization.naics && (
                <div className="organization-detail-item">
                  <span className="detail-label">NAICS:</span>
                  <span className="detail-value">{organization.naics}</span>
                </div>
              )}

              {organization.country && (
                <div className="organization-detail-item">
                  <span className="detail-label">Country:</span>
                  <span className="detail-value">{organization.country}</span>
                </div>
              )}

              {organization.annualSpend !== undefined && (
                <div className="organization-detail-item">
                  <span className="detail-label">Annual Spend:</span>
                  <span className="detail-value">{formatCurrency(organization.annualSpend)}</span>
                </div>
              )}

              {organization.revenue !== undefined && (
                <div className="organization-detail-item">
                  <span className="detail-label">Revenue:</span>
                  <span className="detail-value">{formatCurrency(organization.revenue)}</span>
                </div>
              )}

              {organization.uuid && (
                <div className="organization-detail-item">
                  <span className="detail-label">UUID:</span>
                  <span className="detail-value" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                    {organization.uuid}
                  </span>
                </div>
              )}
            </div>

            {(organization.created || organization.updated) && (
              <div className="organization-timestamps">
                {organization.created && (
                  <div className="timestamp">
                    <span className="timestamp-label">Created:</span>
                    <span className="timestamp-value">{formatDate(organization.created)}</span>
                  </div>
                )}
                {organization.updated && (
                  <div className="timestamp">
                    <span className="timestamp-label">Updated:</span>
                    <span className="timestamp-value">{formatDate(organization.updated)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
